import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useCategories } from '@/hooks/useProducts';
import { useUIStore } from '@/store/uiStore';
import { Checkbox } from '@/components/ui/Checkbox';
import { Select } from '@/components/ui/Select';
import { cn } from '@/lib/cn';
import { getSubcategoryLinks, isMenuLinkActive } from '@/lib/categoryMenu';
import { formatINR } from '@/lib/formatters';
import type { Category } from '@/types/product';

const SIZES: ReadonlyArray<string> = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '32', '36', '38', '40'];

interface ColorChip {
  name: string;
  hex: string;
}

const COLORS: ReadonlyArray<ColorChip> = [
  { name: 'Black', hex: '#0b0b0b' },
  { name: 'White', hex: '#f5f5f5' },
  { name: 'Navy', hex: '#0b1d6e' },
  { name: 'Magenta', hex: '#d4006a' },
  { name: 'Gold', hex: '#c8963e' },
  { name: 'Red', hex: '#c8242c' },
  { name: 'Green', hex: '#2c8c5a' },
  { name: 'Beige', hex: '#d8c8a8' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'price_asc', label: 'Price: Low → High' },
  { value: 'price_desc', label: 'Price: High → Low' },
  { value: 'popular', label: 'Popularity' },
] as const;

const PRICE_MIN_PAISE = 0;
const PRICE_MAX_PAISE = 1_000_000; // ₹10,000 default ceiling

interface ProductFiltersProps {
  /** When true, shows desktop sidebar variant. */
  variant?: 'sidebar' | 'inline';
}

/**
 * One top-level category in the filter sidebar, with its curated subcategories.
 *
 * Subcategories come from the SAME source as the mega-menu (`getSubcategoryLinks`)
 * so the two never drift: apparel verticals (Men/Women/Kids) get hand-curated,
 * keyword-scoped links (`/category/<slug>?q=…`); Jewelry/Bags/Accessories get
 * their sanitised real children. This is why apparel now shows subcategories in
 * the sidebar where before only Jewelry (which had real DB children) did.
 */
function CategoryNode({ category }: { category: Category }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug: routeSlug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();

  const subLinks = useMemo(() => getSubcategoryLinks(category), [category]);
  const hasChildren = subLinks.length > 0;

  const currentQ = params.get('q');
  const onThisCategory = routeSlug === category.slug;
  // The parent is highlighted only on its own "Shop all" view (no keyword
  // refinement) — when a subcategory is active, the parent is not.
  const isParentActive = onThisCategory && !currentQ;

  // Auto-expand the branch we're browsing; keep others collapsed so the list
  // stays scannable. Re-opens if navigation lands here from elsewhere (e.g. the
  // mega-menu) without forcing a user's manual toggle shut.
  const [open, setOpen] = useState(onThisCategory);
  useEffect(() => {
    if (onThisCategory) setOpen(true);
  }, [onThisCategory]);

  return (
    <li className="text-[14px]">
      <div className="flex items-center gap-2">
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Collapse' : 'Expand'}
            aria-expanded={open}
            className="flex h-5 w-5 items-center justify-center text-[color:var(--color-text-muted)] hover:text-white"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={cn('transition-transform', open && 'rotate-90')}
              aria-hidden
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        ) : (
          <span className="inline-block h-5 w-5" aria-hidden />
        )}
        <button
          type="button"
          onClick={() => navigate(`/category/${category.slug}`)}
          aria-current={isParentActive ? 'page' : undefined}
          className={cn(
            'flex-1 truncate py-1.5 text-left transition-colors',
            isParentActive
              ? 'text-[color:var(--color-brand-magenta-light)]'
              : 'text-[color:var(--color-text-secondary)] hover:text-white'
          )}
        >
          {category.name}
        </button>
      </div>
      {hasChildren && open && (
        <ul className="ml-4 mt-1 flex flex-col gap-1 border-l border-white/[0.06] pl-3">
          {subLinks.map((link) => {
            const active = isMenuLinkActive(link.to, location.pathname, currentQ);
            return (
              <li key={link.to}>
                <Link
                  to={link.to}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'block truncate py-1.5 transition-colors',
                    active
                      ? 'text-[color:var(--color-brand-magenta-light)]'
                      : 'text-[color:var(--color-text-secondary)] hover:text-white'
                  )}
                >
                  {link.name}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

function FiltersBody() {
  const { data: categories } = useCategories();
  const [params, setParams] = useSearchParams();

  const selectedSizes = useMemo(() => params.getAll('size'), [params]);
  const selectedColors = useMemo(() => params.getAll('color'), [params]);
  const minPriceStr = params.get('minPrice');
  const maxPriceStr = params.get('maxPrice');
  const sort = params.get('sort') ?? 'newest';

  const [minPrice, setMinPrice] = useState<number>(
    minPriceStr ? Number(minPriceStr) : PRICE_MIN_PAISE
  );
  const [maxPrice, setMaxPrice] = useState<number>(
    maxPriceStr ? Number(maxPriceStr) : PRICE_MAX_PAISE
  );

  useEffect(() => {
    setMinPrice(minPriceStr ? Number(minPriceStr) : PRICE_MIN_PAISE);
    setMaxPrice(maxPriceStr ? Number(maxPriceStr) : PRICE_MAX_PAISE);
  }, [minPriceStr, maxPriceStr]);

  const updateParam = (
    key: string,
    value: string | string[] | null
  ) => {
    const next = new URLSearchParams(params);
    next.delete(key);
    if (value === null || (Array.isArray(value) && value.length === 0)) {
      // already deleted
    } else if (Array.isArray(value)) {
      value.forEach((v) => next.append(key, v));
    } else {
      next.set(key, value);
    }
    next.delete('page');
    setParams(next, { replace: false });
  };

  const toggleArrayParam = (key: 'size' | 'color', val: string) => {
    const current = key === 'size' ? selectedSizes : selectedColors;
    const has = current.includes(val);
    const next = has ? current.filter((x) => x !== val) : [...current, val];
    updateParam(key, next);
  };

  const commitPriceRange = () => {
    const next = new URLSearchParams(params);
    if (minPrice > PRICE_MIN_PAISE) next.set('minPrice', String(minPrice));
    else next.delete('minPrice');
    if (maxPrice < PRICE_MAX_PAISE) next.set('maxPrice', String(maxPrice));
    else next.delete('maxPrice');
    next.delete('page');
    setParams(next, { replace: false });
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Sort */}
      <section className="flex flex-col gap-3">
        <h3 className="text-label-mono text-[color:var(--color-text-muted)]">Sort</h3>
        <Select
          options={SORT_OPTIONS as unknown as { value: string; label: string }[]}
          value={sort}
          onChange={(e) => updateParam('sort', e.target.value)}
          aria-label="Sort products"
        />
      </section>

      {/* Categories */}
      <section className="flex flex-col gap-3">
        <h3 className="text-label-mono text-[color:var(--color-text-muted)]">Category</h3>
        <ul className="flex flex-col gap-1">
          {categories?.map((cat) => (
            <CategoryNode key={cat.id} category={cat} />
          ))}
        </ul>
      </section>

      {/* Price */}
      <section className="flex flex-col gap-3">
        <h3 className="text-label-mono text-[color:var(--color-text-muted)]">Price</h3>
        <div className="flex items-center justify-between text-[12px] font-[family-name:var(--font-label)] text-[color:var(--color-text-secondary)]">
          <span>{formatINR(minPrice)}</span>
          <span>{formatINR(maxPrice)}</span>
        </div>
        <div className="flex flex-col gap-2">
          <input
            type="range"
            min={PRICE_MIN_PAISE}
            max={PRICE_MAX_PAISE}
            step={50_000}
            value={minPrice}
            onChange={(e) => setMinPrice(Math.min(Number(e.target.value), maxPrice))}
            onMouseUp={commitPriceRange}
            onTouchEnd={commitPriceRange}
            className="accent-[color:var(--color-brand-magenta)]"
            aria-label="Minimum price"
          />
          <input
            type="range"
            min={PRICE_MIN_PAISE}
            max={PRICE_MAX_PAISE}
            step={50_000}
            value={maxPrice}
            onChange={(e) => setMaxPrice(Math.max(Number(e.target.value), minPrice))}
            onMouseUp={commitPriceRange}
            onTouchEnd={commitPriceRange}
            className="accent-[color:var(--color-brand-magenta)]"
            aria-label="Maximum price"
          />
        </div>
      </section>

      {/* Sizes */}
      <section className="flex flex-col gap-3">
        <h3 className="text-label-mono text-[color:var(--color-text-muted)]">Size</h3>
        <div className="flex flex-col gap-2">
          {SIZES.map((size) => (
            <Checkbox
              key={size}
              label={size}
              checked={selectedSizes.includes(size)}
              onChange={() => toggleArrayParam('size', size)}
            />
          ))}
        </div>
      </section>

      {/* Colors */}
      <section className="flex flex-col gap-3">
        <h3 className="text-label-mono text-[color:var(--color-text-muted)]">Color</h3>
        <div className="flex flex-wrap gap-2">
          {COLORS.map((c) => {
            const active = selectedColors.includes(c.name);
            return (
              <button
                key={c.name}
                type="button"
                aria-label={c.name}
                aria-pressed={active}
                onClick={() => toggleArrayParam('color', c.name)}
                className={cn(
                  'h-5 w-5 rounded-full border transition-[transform,box-shadow]',
                  active
                    ? 'scale-110 border-white shadow-[0_0_0_2px_var(--color-brand-magenta)]'
                    : 'border-white/20'
                )}
                style={{ backgroundColor: c.hex }}
              />
            );
          })}
        </div>
      </section>
    </div>
  );
}

export function ProductFilters({ variant = 'sidebar' }: ProductFiltersProps) {
  const isOpen = useUIStore((s) => s.isFilterDrawerOpen);
  const close = useUIStore((s) => s.closeFilterDrawer);
  const open = useUIStore((s) => s.openFilterDrawer);
  const [params] = useSearchParams();

  const activeCount = useMemo(() => {
    let c = 0;
    if (params.get('category')) c++;
    c += params.getAll('size').length;
    c += params.getAll('color').length;
    if (params.get('minPrice')) c++;
    if (params.get('maxPrice')) c++;
    return c;
  }, [params]);

  if (variant === 'sidebar') {
    return (
      <aside
        aria-label="Filters"
        className="hidden w-[260px] shrink-0 lg:block"
      >
        <div className="sticky top-[88px] max-h-[calc(100vh-104px)] overflow-y-auto pr-3">
          <FiltersBody />
        </div>
      </aside>
    );
  }

  // Inline mobile trigger + drawer
  return (
    <>
      <button
        type="button"
        onClick={open}
        className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-4 py-2.5 text-[13px] font-medium text-white transition-colors hover:bg-white/10 lg:hidden"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="6" y1="12" x2="18" y2="12" />
          <line x1="9" y1="18" x2="15" y2="18" />
        </svg>
        Filters
        {activeCount > 0 && (
          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[color:var(--color-brand-magenta)] px-1.5 text-[11px] font-semibold text-white">
            {activeCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-[120] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <button
              type="button"
              aria-label="Close filters"
              onClick={close}
              className="absolute inset-0 bg-[color:var(--color-bg-deep)]/70 backdrop-blur-sm"
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl border-t border-white/10 bg-[color:var(--color-bg-mid)] p-6"
              initial={{ y: '100%' }}
              animate={{ y: '0%' }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            >
              <header className="mb-6 flex items-center justify-between">
                <h2 className="font-[family-name:var(--font-editorial)] text-xl text-white">
                  Filters
                </h2>
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close filters"
                  className="rounded-md p-2 text-[color:var(--color-text-secondary)] hover:bg-white/10 hover:text-white"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </header>
              <FiltersBody />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
